/* ==========================================================================
   X-HEALTHY — product-card.js
   <product-card>     quick add (single-variant) and quick view triggers
   <wishlist-button>  heart toggle backed by theme.wishlist (localStorage)
   Load on any section that renders snippets/card-product.
   ========================================================================== */

(() => {
  const { theme } = window;

  /* ---------- Lazy script loader ---------- */
  const loaded = {};
  theme.loadScript =
    theme.loadScript ||
    ((src) => {
      if (!src) return Promise.resolve();
      if (!loaded[src]) {
        loaded[src] = new Promise((resolve, reject) => {
          if (document.querySelector(`script[src="${src}"]`)) return resolve();
          const script = document.createElement('script');
          script.src = src;
          script.defer = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      return loaded[src];
    });

  /* ---------- Quick view ---------- */
  theme.quickView =
    theme.quickView ||
    (async (url, trigger) => {
      const modal = document.getElementById('QuickView');
      if (!modal) {
        window.location.href = url;
        return;
      }
      const content = modal.querySelector('[data-quick-view-content]');
      content.innerHTML = content.dataset.skeleton || '';
      content.setAttribute('aria-busy', 'true');
      modal.open(trigger);
      try {
        const [doc] = await Promise.all([
          theme.utils.fetchSection(url, 'quick-view'),
          theme.loadScript(theme.assets?.productForm),
          theme.loadScript(theme.assets?.slider),
        ]);
        const section = doc.querySelector('.quick-view');
        if (!section) throw new Error('Quick view unavailable');
        content.replaceChildren(section);
        content.removeAttribute('aria-busy');
        const title = content.querySelector('[data-quick-view-title]');
        if (title) title.focus({ preventScroll: true });
      } catch (error) {
        modal.close();
        window.location.href = url;
      }
    });

  /* ---------- <product-card> ---------- */
  if (!customElements.get('product-card')) {
    customElements.define(
      'product-card',
      class ProductCard extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form[data-quick-add]');
          this.form?.addEventListener('submit', (event) => this.onQuickAdd(event));
          this.querySelectorAll('[data-quick-view]').forEach((button) =>
            button.addEventListener('click', () => theme.quickView(button.dataset.quickView, button))
          );
        }

        async onQuickAdd(event) {
          event.preventDefault();
          const button = this.form.querySelector('[type="submit"]');
          if (button.getAttribute('aria-busy') === 'true') return;
          button.setAttribute('aria-busy', 'true');
          try {
            const { item } = await theme.cart.add(new FormData(this.form));
            theme.utils.announce(`${theme.strings.addedToCart}: ${item.product_title}`);
            if (!document.getElementById('CartDrawer')) {
              theme.toast({
                message: `${theme.strings.addedToCart}`,
                action: { label: theme.strings.viewCart, url: theme.routes.cart },
              });
            }
          } catch (error) {
            theme.toast({ message: error.message, type: 'error' });
          } finally {
            button.removeAttribute('aria-busy');
          }
        }
      }
    );
  }

  /* ---------- <wishlist-button> ---------- */
  if (!customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class WishlistButton extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          this.handle = this.dataset.handle;
          this.render();
          this.button.addEventListener('click', () => {
            const added = theme.wishlist.toggle(this.handle);
            theme.toast({
              message: added ? theme.strings.wishlistAdded : theme.strings.wishlistRemoved,
              action: added ? { label: theme.strings.viewWishlist, url: theme.routes.wishlist } : null,
            });
          });
          this.unsubscribe = theme.events.on('wishlist:updated', () => this.render());
        }

        disconnectedCallback() {
          this.unsubscribe?.();
        }

        render() {
          const active = theme.wishlist.has(this.handle);
          this.button.setAttribute('aria-pressed', String(active));
          this.button.setAttribute('aria-label', active ? this.button.dataset.labelRemove : this.button.dataset.labelAdd);
        }
      }
    );
  }
})();
